import { QueryClient } from '@tanstack/react-query';
import { create } from '@bufbuild/protobuf';
import { ListMetricsRequest_TargetSchema } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/aqora_service_pb';
import { LoaderFunctionArgs } from 'react-router';
import { MetricType } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/metrics_pb';
import { System } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/system_pb';
import {
  chartMetricQuery,
  systemModeMetricQuery,
} from '~/shared/api/metrics.queries';
import { getSystemQuery } from '~/shared/api/system.queries';
import { MetricDataSet } from './types';
import { METRICS_DATA_PARAM_NAME } from '~/shared/models/create-routes';
import { assertIsDefined } from '~/types/assert-type';
import { checkAuthorization } from '~/shared/auth/auth-utils';
import { ChartMetricModel } from '~/shared/models/metrics/ChartMetricModel';
import { SystemModeMetricModel } from '~/shared/models/metrics/SystemModeMetricModel';
import { systemBehaviourQuery } from '~/pages/Metrics/api/behaviour.queries';
import { SystemBehaviourModel } from '~/pages/Metrics/models/SystemBehaviourModel';
import { getSafeObjectFromUrl } from '~/shared/utils/get-safe-object-from-url';
import { ResourceNameIdConverter } from '~/shared/models/ResourceNameIdConverter';
import { createSystemBehaviourTargets } from '~/pages/Metrics/create-system-behaviour-targets';

export type MetricsSystemLoaderResult = {
  system: System;
};

export function metricsSystemsLoader(queryClient: QueryClient) {
  return async ({
    request,
    params,
  }: LoaderFunctionArgs): Promise<MetricsSystemLoaderResult> => {
    await checkAuthorization(queryClient, request);

    const { systemId } = params;

    assertIsDefined(systemId);

    const system = await queryClient.ensureQueryData(getSystemQuery(systemId));

    return { system };
  };
}

export type MetricsLoaderResult = {
  system: System;
  metrics: ChartMetricModel[];
  historicSystemMode: SystemModeMetricModel;
  systemBehaviour?: SystemBehaviourModel;
};

export function metricsLoader(queryClient: QueryClient) {
  return async ({
    request,
    params,
  }: LoaderFunctionArgs): Promise<MetricsLoaderResult> => {
    await checkAuthorization(queryClient, request);

    const { systemId } = params;

    assertIsDefined(systemId);

    const { searchParams } = new URL(request.url);

    const data = getSafeObjectFromUrl<MetricDataSet>(
      searchParams,
      METRICS_DATA_PARAM_NAME,
    );

    const promises = data
      .filter(({ metricType }) => !!metricType)
      .map(({ resourceName, metricType }) => {
        assertIsDefined(metricType);
        return queryClient.ensureQueryData(
          chartMetricQuery({
            target: create(ListMetricsRequest_TargetSchema, {
              resourceName,
              types: [MetricType[metricType]],
            }),
          }),
        );
      });

    const historicSystemModePromise = queryClient.ensureQueryData(
      systemModeMetricQuery({
        target: create(ListMetricsRequest_TargetSchema, {
          resourceName: ResourceNameIdConverter.systemIdToName(systemId),
          types: [MetricType.SYSTEM_MODE],
        }),
      }),
    );

    const systemBehaviourTargets = createSystemBehaviourTargets(data);

    const systemBehaviourPromise =
      systemBehaviourTargets.length > 0
        ? queryClient.ensureQueryData(
            systemBehaviourQuery({
              targets: systemBehaviourTargets,
            }),
          )
        : undefined;

    const systemPromise = queryClient.ensureQueryData(getSystemQuery(systemId));
    const [system, historicSystemMode, systemBehaviour, ...metrics] =
      await Promise.all([
        systemPromise,
        historicSystemModePromise,
        systemBehaviourPromise,
        ...promises,
      ]);

    return {
      system,
      metrics,
      historicSystemMode,
      systemBehaviour,
    };
  };
}
